import React, { useEffect, useMemo, useRef, useState } from "react";
import YhSelect from "../../../../../../ui/YhSelect";
import YhInput from "../../../../../../ui/YhInput";
import YhMultiSelect from "../../../../../../ui/YhMultiSelect";
import { buildPublicFormUrl } from "../../../../feedbackPublicUrl";
import { FormShareLinkList } from "../../../FormShareLinks";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../../helpers/methods";
import { listOrgLeadSources, fetchAuthKeyTemplates } from "../../../../../../services/organizationService";
import { uploadFormMedia } from "../../../../formMediaService";
import {
  getGoogleSheetStatus,
  getGoogleSheetAuthUrl,
  disconnectGoogleSheet,
  listGoogleSpreadsheets,
  listGoogleSheetTabs,
  createGoogleSpreadsheet,
  testGoogleSheetConnection,
} from "../../StepperService";

const MAX_MEDIA_BYTES = 50 * 1024 * 1024;

const THEME_COLORS = [
  "#841C39",
  "#3B82F6",
  "#22C55E",
  "#F97316",
  "#EC4899",
  "#9CA3AF",
];

export type MediaPanelState = {
  /** Show the image/video panel next to the public form */
  enabled: boolean;
  /** Side the panel sits on, on desktop */
  position: "left" | "right";
  mediaType: "video" | "image";
  /** Video URL, or legacy single image (also kept in sync with desktop image) */
  mediaUrl: string;
  /** Web / desktop side-panel image — covers the left/right column */
  desktopImageUrl: string;
  /** Mobile side-panel image — covers the top banner band */
  mobileImageUrl: string;
  /** Empty falls back to the organization name */
  brandTitle: string;
  /** Empty falls back to the organization address · website */
  brandSubtitle: string;
  eyebrow: string;
  headline: string;
  /**
   * Per-element visibility, kept separate from the text so hiding something
   * does not lose its wording.
   */
  showBrandTitle: boolean;
  showBrandSubtitle: boolean;
  showEyebrow: boolean;
  showHeadline: boolean;
  /** Show the form name as the heading above the questions */
  showFormTitle: boolean;
  showLogo: boolean;
  showPartnerLabel: boolean;
  showDescription: boolean;
  showOffer: boolean;
  showUnitField: boolean;
  offerTitle: string;
  offerText: string;
  offerImageUrl: string;
  offerScope: "all" | "projects";
  offerProjectIds: string[];
};

export type FormSeoState = {
  /** Browser tab / search result title — blank uses the form name */
  metaTitle: string;
  metaDescription: string;
  /** Share card title — blank reuses the meta title */
  ogTitle: string;
  ogDescription: string;
  /** Share image — blank reuses the side panel image */
  ogImageUrl: string;
};

export type GoogleSheetState = {
  /** Append each feedback / registration submission to the sheet */
  enabled: boolean;
  /** Selected spreadsheet id (from connected Google account) */
  spreadsheetId: string;
  /** Tab / sheet name inside the spreadsheet */
  sheetName: string;
  spreadsheetTitle: string;
  connectedGoogleEmail: string;
  connectedUserId: string;
};

export type FormSettingsState = {
  description: string;
  theme: string;
  enableForm: boolean;
  showProgressBar: boolean;
  allowMultiple: boolean;
  enableCaptcha: boolean;
  enablePaymentMethod: boolean;
  enableAadhaarVerification: boolean;
  /** normal | feedback | registration | website (landing popup) */
  formType: "normal" | "feedback" | "registration" | "website";
  /** steps = one question per page; single = every field on one page */
  formLayout: "steps" | "single";
  /** 0 = no expiry; otherwise max days for customer token links */
  tokenMaxDays: number;
  /** Org lead source_name applied as default / ?src= */
  defaultLeadSource: string;
  /** After public submit, send Engage WhatsApp (welcome / thank-you) */
  sendWhatsAppOnSubmit: boolean;
  onSubmitWhatsappTemplateWid: string;
  onSubmitWhatsappTemplateName: string;
  mediaPanel: MediaPanelState;
  seo: FormSeoState;
  googleSheet: GoogleSheetState;
  publicUrl?: string;
  publicSlug?: string;
  orgSlug?: string;
  projectSlug?: string;
  formScope?: string;
};

type Props = {
  settings: FormSettingsState;
  onChange: (next: FormSettingsState) => void;
  /** Active form organization — loads lead sources for default picker */
  organizationId?: string;
  /** Website form already exists for this org — hide the type on create */
  hasWebsiteForm?: boolean;
  /** Editing the website form — type is locked */
  lockWebsiteType?: boolean;
  projectOptions?: { label: string; value: string }[];
};

const Toggle = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) => (
  <label className="flex cursor-pointer items-center justify-between gap-3 py-2.5">
    <span className="text-sm font-medium text-[#374151]">{label}</span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 shrink-0 rounded-full transition ${
        checked ? "bg-[#841C39]" : "bg-[#D1D5DB]"
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${
          checked ? "left-[22px]" : "left-0.5"
        }`}
      />
    </button>
  </label>
);

export const defaultMediaPanel = (): MediaPanelState => ({
  enabled: true,
  position: "left",
  mediaType: "video",
  mediaUrl: "",
  desktopImageUrl: "",
  mobileImageUrl: "",
  brandTitle: "",
  brandSubtitle: "",
  eyebrow: "",
  headline: "",
  showBrandTitle: true,
  showBrandSubtitle: true,
  showEyebrow: true,
  showHeadline: true,
  showFormTitle: true,
  showLogo: true,
  showPartnerLabel: true,
  showDescription: true,
  showOffer: false,
  showUnitField: true,
  offerTitle: "",
  offerText: "",
  offerImageUrl: "",
  offerScope: "all",
  offerProjectIds: [],
});

export const defaultFormSeo = (): FormSeoState => ({
  metaTitle: "",
  metaDescription: "",
  ogTitle: "",
  ogDescription: "",
  ogImageUrl: "",
});

export const defaultGoogleSheet = (): GoogleSheetState => ({
  enabled: false,
  spreadsheetId: "",
  sheetName: "Sheet1",
  spreadsheetTitle: "",
  connectedGoogleEmail: "",
  connectedUserId: "",
});

/** Placeholders mirror what the public form renders when a field is blank. */
const PANEL_TEXT_FIELDS: {
  key: "brandTitle" | "brandSubtitle" | "eyebrow" | "headline";
  showKey:
    | "showBrandTitle"
    | "showBrandSubtitle"
    | "showEyebrow"
    | "showHeadline";
  label: string;
  placeholder: string;
}[] = [
  {
    key: "brandTitle",
    showKey: "showBrandTitle",
    label: "Brand title",
    placeholder: "Organization name",
  },
  {
    key: "brandSubtitle",
    showKey: "showBrandSubtitle",
    label: "Brand subtitle",
    placeholder: "Organization address · website",
  },
  {
    key: "eyebrow",
    showKey: "showEyebrow",
    label: "Panel eyebrow",
    placeholder: "Real estate registration",
  },
  {
    key: "headline",
    showKey: "showHeadline",
    label: "Panel headline",
    placeholder: "Begin your property journey",
  },
];

export const defaultFormSettings = (): FormSettingsState => ({
  description: "",
  theme: "#841C39",
  enableForm: true,
  showProgressBar: true,
  allowMultiple: false,
  enableCaptcha: false,
  enablePaymentMethod: false,
  enableAadhaarVerification: false,
  formType: "normal",
  formLayout: "steps",
  tokenMaxDays: 90,
  defaultLeadSource: "",
  sendWhatsAppOnSubmit: false,
  onSubmitWhatsappTemplateWid: "",
  onSubmitWhatsappTemplateName: "",
  mediaPanel: defaultMediaPanel(),
  seo: defaultFormSeo(),
  googleSheet: defaultGoogleSheet(),
  publicUrl: "",
  publicSlug: "",
  orgSlug: "",
  projectSlug: "",
  formScope: "",
});

const FormSettingsPanel = ({
  settings,
  onChange,
  organizationId,
  hasWebsiteForm = false,
  lockWebsiteType = false,
  projectOptions = [],
}: Props) => {
  const patch = (partial: Partial<FormSettingsState>) =>
    onChange({ ...settings, ...partial });

  const media = settings.mediaPanel || defaultMediaPanel();
  const patchMedia = (partial: Partial<MediaPanelState>) =>
    patch({ mediaPanel: { ...defaultMediaPanel(), ...media, ...partial } });

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const desktopInputRef = useRef<HTMLInputElement | null>(null);
  const mobileInputRef = useRef<HTMLInputElement | null>(null);
  const offerInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState<
    "video" | "desktop" | "mobile" | "offer" | null
  >(null);
  const [waTemplates, setWaTemplates] = useState<
    { label: string; value: string; name: string }[]
  >([]);
  const [loadingWaTemplates, setLoadingWaTemplates] = useState(false);

  useEffect(() => {
    const isPublic =
      settings.formType === "feedback" || settings.formType === "registration";
    if (!isPublic || !organizationId) {
      setWaTemplates([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingWaTemplates(true);
      try {
        const res = await fetchAuthKeyTemplates(String(organizationId), "whatsapp");
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.templates)
            ? res.templates
            : [];
        if (cancelled) return;
        setWaTemplates(
          list
            .map((t: any) => {
              const wid = String(t?.wid || t?.id || t?.template_id || "").trim();
              if (!wid) return null;
              const name = String(t?.name || t?.template_name || wid).trim();
              return {
                value: wid,
                name,
                label: name !== wid ? `${name} (${wid})` : name,
              };
            })
            .filter(Boolean) as { label: string; value: string; name: string }[]
        );
      } catch (_) {
        if (!cancelled) setWaTemplates([]);
      } finally {
        if (!cancelled) setLoadingWaTemplates(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [settings.formType, organizationId]);

  const uploadMediaFile = async (
    file: File,
    slot: "video" | "desktop" | "mobile" | "offer"
  ) => {
    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");
    if (slot === "video") {
      if (!isVideo && !isImage) {
        openErrorNotification("Choose an image or a video file");
        return;
      }
    } else if (!isImage) {
      openErrorNotification("Choose an image file");
      return;
    }
    if (file.size > MAX_MEDIA_BYTES) {
      openErrorNotification("File is too large. Keep it under 50 MB.");
      return;
    }
    setUploading(true);
    setUploadingSlot(slot);
    try {
      const url = await uploadFormMedia(file);
      if (slot === "offer") {
        patchMedia({ offerImageUrl: url, showOffer: true });
      } else if (slot === "desktop") {
        patchMedia({
          mediaType: "image",
          desktopImageUrl: url,
          mediaUrl: url,
        });
      } else if (slot === "mobile") {
        patchMedia({
          mediaType: "image",
          mobileImageUrl: url,
        });
      } else {
        patchMedia({
          mediaUrl: url,
          mediaType: isVideo ? "video" : "image",
          ...(isImage
            ? { desktopImageUrl: url }
            : { desktopImageUrl: "", mobileImageUrl: "" }),
        });
      }
      openSuccessNotification("Media uploaded");
    } catch (error: any) {
      openErrorNotification(error?.message || "Unable to upload the file");
    } finally {
      setUploading(false);
      setUploadingSlot(null);
    }
  };

  const handleMediaFile = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await uploadMediaFile(file, "video");
  };

  const handleDesktopImage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await uploadMediaFile(file, "desktop");
  };

  const handleMobileImage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await uploadMediaFile(file, "mobile");
  };

  const handleOfferImage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await uploadMediaFile(file, "offer");
  };

  // Prefer dedicated desktop field; fall back to legacy mediaUrl for old forms.
  const desktopPreview =
    media.desktopImageUrl ||
    (media.mediaType === "image" ? media.mediaUrl : "");
  const mobilePreview = media.mobileImageUrl;

  const seo = settings.seo || defaultFormSeo();
  const patchSeo = (partial: Partial<FormSeoState>) =>
    patch({ seo: { ...seo, ...partial } });

  const googleSheet = settings.googleSheet || defaultGoogleSheet();
  const patchGoogleSheet = (partial: Partial<GoogleSheetState>) =>
    patch({ googleSheet: { ...googleSheet, ...partial } });

  const [sheetStatus, setSheetStatus] = useState<{
    connected: boolean;
    googleEmail: string;
    userId: string;
    configured?: boolean;
  } | null>(null);
  const [sheetTesting, setSheetTesting] = useState(false);
  const [sheetConnecting, setSheetConnecting] = useState(false);
  const [spreadsheetOptions, setSpreadsheetOptions] = useState<
    { id: string; name: string }[]
  >([]);
  const [tabOptions, setTabOptions] = useState<string[]>([]);
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [creatingSheet, setCreatingSheet] = useState(false);

  const refreshSheetConnection = async () => {
    try {
      const res = await getGoogleSheetStatus();
      if (res?.success && res?.data) {
        const next = {
          connected: !!res.data.connected,
          googleEmail: String(res.data.googleEmail || ""),
          userId: String(res.data.userId || ""),
        };
        setSheetStatus(next);
        if (next.connected) {
          patchGoogleSheet({
            connectedGoogleEmail: next.googleEmail,
            connectedUserId: next.userId,
          });
        }
        return next;
      }
    } catch (_) {
      /* ignore */
    }
    return null;
  };

  const loadSpreadsheets = async () => {
    setLoadingSheets(true);
    try {
      const res = await listGoogleSpreadsheets();
      const files = Array.isArray(res?.data) ? res.data : [];
      setSpreadsheetOptions(
        files.map((f: any) => ({
          id: String(f.id),
          name: String(f.name || f.id),
        }))
      );
    } catch (error: any) {
      openErrorNotification(
        error?.message || "Unable to load your Google Sheets"
      );
    } finally {
      setLoadingSheets(false);
    }
  };

  const loadTabsFor = async (spreadsheetId: string) => {
    if (!spreadsheetId) {
      setTabOptions([]);
      return;
    }
    try {
      const res = await listGoogleSheetTabs(spreadsheetId);
      const tabs = Array.isArray(res?.data?.tabs) ? res.data.tabs : [];
      setTabOptions(tabs.map((t: any) => String(t)));
      const title = String(res?.data?.title || "");
      if (title) {
        patchGoogleSheet({ spreadsheetTitle: title });
      }
      if (tabs.length && !tabs.includes(googleSheet.sheetName)) {
        patchGoogleSheet({ sheetName: String(tabs[0]) });
      }
    } catch (error: any) {
      openErrorNotification(error?.message || "Unable to load sheet tabs");
    }
  };

  const handleConnectGoogleSheet = async () => {
    setSheetConnecting(true);
    try {
      const returnTo = window.location.href.split("#")[0].split("?")[0];
      const res = await getGoogleSheetAuthUrl(returnTo);
      const url = res?.data?.url || res?.url;
      if (!url) {
        openErrorNotification(res?.message || "Unable to start Google connect");
        return;
      }
      window.location.href = url;
    } catch (error: any) {
      openErrorNotification(
        error?.message || "Unable to start Google connect"
      );
    } finally {
      setSheetConnecting(false);
    }
  };

  const handleDisconnectGoogleSheet = async () => {
    try {
      const res = await disconnectGoogleSheet();
      if (res?.success) {
        setSheetStatus({ connected: false, googleEmail: "", userId: "" });
        setSpreadsheetOptions([]);
        setTabOptions([]);
        patchGoogleSheet({
          enabled: false,
          spreadsheetId: "",
          sheetName: "Sheet1",
          spreadsheetTitle: "",
          connectedGoogleEmail: "",
          connectedUserId: "",
        });
        openSuccessNotification("Google account disconnected");
      } else {
        openErrorNotification(res?.message || "Unable to disconnect");
      }
    } catch (error: any) {
      openErrorNotification(error?.message || "Unable to disconnect");
    }
  };

  const handleCreateSpreadsheet = async () => {
    setCreatingSheet(true);
    try {
      const res = await createGoogleSpreadsheet(
        `${settings.formType || "form"} responses`
      );
      if (!res?.success || !res?.data?.id) {
        openErrorNotification(res?.message || "Unable to create sheet");
        return;
      }
      await loadSpreadsheets();
      patchGoogleSheet({
        enabled: true,
        spreadsheetId: String(res.data.id),
        spreadsheetTitle: String(res.data.name || ""),
        sheetName: String(res.data.sheetName || "Sheet1"),
        connectedGoogleEmail:
          sheetStatus?.googleEmail || googleSheet.connectedGoogleEmail,
        connectedUserId: sheetStatus?.userId || googleSheet.connectedUserId,
      });
      setTabOptions([String(res.data.sheetName || "Sheet1")]);
      openSuccessNotification("Spreadsheet created and selected");
    } catch (error: any) {
      openErrorNotification(error?.message || "Unable to create sheet");
    } finally {
      setCreatingSheet(false);
    }
  };

  const handleTestGoogleSheet = async () => {
    if (!googleSheet.spreadsheetId?.trim()) {
      openErrorNotification("Select a Google Sheet first");
      return;
    }
    setSheetTesting(true);
    try {
      const res = await testGoogleSheetConnection({
        spreadsheetId: googleSheet.spreadsheetId,
        sheetName: googleSheet.sheetName || "Sheet1",
      });
      if (res?.success) {
        openSuccessNotification(res.message || "Sheet ready");
        if (!googleSheet.enabled) {
          patchGoogleSheet({ enabled: true });
        }
      } else {
        openErrorNotification(res?.message || "Could not access the sheet");
      }
    } catch (error: any) {
      openErrorNotification(
        error?.message || "Could not access the sheet"
      );
    } finally {
      setSheetTesting(false);
    }
  };

  const ogInputRef = useRef<HTMLInputElement | null>(null);
  const [ogUploading, setOgUploading] = useState(false);

  const handleOgFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      openErrorNotification("Choose an image file");
      return;
    }
    if (file.size > MAX_MEDIA_BYTES) {
      openErrorNotification("File is too large. Keep it under 50 MB.");
      return;
    }
    setOgUploading(true);
    try {
      const url = await uploadFormMedia(file);
      patchSeo({ ogImageUrl: url });
      openSuccessNotification("Share image uploaded");
    } catch (error: any) {
      openErrorNotification(error?.message || "Unable to upload the file");
    } finally {
      setOgUploading(false);
    }
  };

  const [sourceOptions, setSourceOptions] = useState<
    { label: string; value: string }[]
  >([{ label: "None (no default)", value: "" }]);
  const [loadingSources, setLoadingSources] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!organizationId) {
        setSourceOptions([{ label: "None (no default)", value: "" }]);
        return;
      }
      setLoadingSources(true);
      try {
        const res = await listOrgLeadSources(organizationId);
        const list = res?.data?.items || res?.data || res?.items || [];
        const opts = (Array.isArray(list) ? list : [])
          .filter((s: any) => s.isActive !== false && String(s.status) !== "0")
          .map((s: any) => ({
            label: String(s.source_name),
            value: String(s.source_name),
          }));
        if (!cancelled) {
          setSourceOptions([
            { label: "None (no default)", value: "" },
            ...opts,
          ]);
        }
      } catch (_) {
        if (!cancelled) {
          setSourceOptions([{ label: "None (no default)", value: "" }]);
        }
      } finally {
        if (!cancelled) setLoadingSources(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const publicLink = useMemo(
    () =>
      buildPublicFormUrl({
        publicUrl: settings.publicUrl,
        orgSlug: settings.orgSlug,
        projectSlug: settings.projectSlug,
        publicSlug: settings.publicSlug,
        formType: settings.formType,
        defaultLeadSource: settings.defaultLeadSource,
      }),
    [
      settings.publicUrl,
      settings.orgSlug,
      settings.projectSlug,
      settings.publicSlug,
      settings.formType,
      settings.defaultLeadSource,
    ]
  );

  const isPublicForm =
    settings.formType === "feedback" || settings.formType === "registration";

  useEffect(() => {
    if (!isPublicForm) return;
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const justConnected = params.get("googleSheet") === "connected";
      const emailFromQuery = String(params.get("email") || "").trim();

      // Optimistic UI right after OAuth redirect (don't wait on status)
      if (justConnected) {
        setSheetStatus({
          connected: true,
          googleEmail: emailFromQuery,
          userId: "",
        });
        patchGoogleSheet({
          enabled: true,
          connectedGoogleEmail: emailFromQuery || googleSheet.connectedGoogleEmail,
        });
        openSuccessNotification(
          emailFromQuery
            ? `Google connected as ${emailFromQuery}`
            : "Google account connected"
        );
        params.delete("googleSheet");
        params.delete("email");
        const clean = `${window.location.pathname}${
          params.toString() ? `?${params}` : ""
        }${window.location.hash || ""}`;
        window.history.replaceState({}, "", clean);
      }

      try {
        const res = await getGoogleSheetStatus();
        if (cancelled) return;
        const data = res?.data || {};
        const email = String(
          data.googleEmail || emailFromQuery || ""
        ).trim();
        const connected = !!(data.connected || email);
        const next = {
          connected,
          googleEmail: email,
          userId: String(data.userId || ""),
          configured: data.configured !== false,
        };
        setSheetStatus(next);
        if (connected) {
          patchGoogleSheet({
            connectedGoogleEmail: email || googleSheet.connectedGoogleEmail,
            connectedUserId: next.userId || googleSheet.connectedUserId,
            ...(justConnected ? { enabled: true } : {}),
          });
          await loadSpreadsheets();
          const sheetId =
            (settings.googleSheet || googleSheet).spreadsheetId || "";
          if (sheetId) {
            await loadTabsFor(sheetId);
          }
        }
      } catch (_) {
        /* keep optimistic connected state if OAuth just finished */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublicForm]);

  const isGoogleSheetConnected = !!(
    sheetStatus?.connected ||
    sheetStatus?.googleEmail ||
    googleSheet.connectedGoogleEmail ||
    googleSheet.connectedUserId
  );

  const linkedSheetUrl = googleSheet.spreadsheetId
    ? `https://docs.google.com/spreadsheets/d/${encodeURIComponent(
        googleSheet.spreadsheetId
      )}/edit`
    : "";

  return (
    <div className="flex h-full flex-col gap-5 overflow-auto p-4">
      <div>
        <YhSelect
          label="Form type"
          value={settings.formType || "normal"}
          disabled={lockWebsiteType}
          onChange={(e: any) => {
            const raw = String(e?.target?.value || "normal");
            const v =
              raw === "feedback" ||
              raw === "registration" ||
              raw === "website"
                ? raw
                : "normal";
            if (v === "website" && hasWebsiteForm && !lockWebsiteType) {
              openErrorNotification(
                "This organization already has a website form. Edit that form instead."
              );
              return;
            }
            const next: Partial<FormSettingsState> = {
              formType: v as FormSettingsState["formType"],
            };
            if (v === "website") {
              next.formLayout = "single";
              next.theme = settings.theme || "#841C39";
            }
            patch(next);
          }}
          options={
            lockWebsiteType
              ? [{ label: "Website form", value: "website" }]
              : [
                  { label: "Normal (lead form)", value: "normal" },
                  ...(hasWebsiteForm
                    ? []
                    : [{ label: "Website form", value: "website" }]),
                  { label: "Feedback form", value: "feedback" },
                  { label: "Registration form", value: "registration" },
                ]
          }
        />
        {settings.formType === "website" ? (
          <p className="mt-1.5 text-[11px] leading-relaxed text-[#6B7280]">
            This is the only website form for the Yhataw organization. It opens
            on every project landing page. Edit field labels here — it cannot
            be deleted or duplicated. Leads go to whoever that project is
            assigned to.
          </p>
        ) : null}
      </div>

      <div>
        <YhSelect
          label="Default lead source"
          clearable
          value={settings.defaultLeadSource || ""}
          onChange={(e: any) =>
            patch({ defaultLeadSource: String(e?.target?.value || "") })
          }
          options={
            loadingSources
              ? [{ label: "Loading sources…", value: "" }]
              : sourceOptions
          }
        />
        <p className="mt-1.5 text-[11px] leading-relaxed text-[#6B7280]">
          Applied to leads created from this form and as{" "}
          <code className="rounded bg-[#F3F4F6] px-1">?src=</code> on the
          public link. Manage sources under Settings → Meta Data → Lead
          Sources.
          {!organizationId ? (
            <span className="block text-[#B45309]">
              Select an organization on the form to load sources.
            </span>
          ) : null}
        </p>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
          Form Description
        </label>
        <textarea
          value={settings.description}
          onChange={(e) => patch({ description: e.target.value })}
          placeholder="Write a short summary of this form..."
          rows={4}
          className="w-full resize-none rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827] outline-none transition placeholder:text-[#9CA3AF] focus:border-[#841C39] focus:ring-2 focus:ring-[#841C39]/20"
        />
        {settings.formType === "website" ? (
          <p className="mt-1.5 text-[11px] leading-relaxed text-[#6B7280]">
            Shown under the form name on the landing popup when description is
            enabled below.
          </p>
        ) : null}
      </div>

      {settings.formType === "website" ? (
        <div className="space-y-3 rounded-xl border border-[#F9F0F4] bg-[#FDFBFC] p-3">
          <p className="text-sm font-semibold text-[#111827]">
            Landing popup
          </p>
          <Toggle
            label="Show form name"
            checked={media.showFormTitle !== false}
            onChange={(showFormTitle) => patchMedia({ showFormTitle })}
          />
          <Toggle
            label="Show form description"
            checked={media.showDescription !== false}
            onChange={(showDescription) => patchMedia({ showDescription })}
          />
          <Toggle
            label="Show developer logo"
            checked={media.showLogo !== false}
            onChange={(showLogo) => patchMedia({ showLogo })}
          />
          <Toggle
            label="Show Authorized Channel Partner"
            checked={media.showPartnerLabel !== false}
            onChange={(showPartnerLabel) => patchMedia({ showPartnerLabel })}
          />
          <Toggle
            label="Capture BHK / unit type"
            checked={media.showUnitField !== false}
            onChange={(showUnitField) => patchMedia({ showUnitField })}
          />
          <p className="-mt-1 text-[11px] leading-relaxed text-[#6B7280]">
            Residential projects show BHK from that project’s inventory.
            Commercial projects show unit type instead.
          </p>
          <Toggle
            label="Run an offer / promotion"
            checked={!!media.showOffer}
            onChange={(showOffer) => patchMedia({ showOffer })}
          />
          {media.showOffer ? (
            <div className="space-y-3 rounded-xl border border-[#F3D5DE] bg-white p-3">
              <YhInput
                label="Offer title"
                placeholder="Limited-time offer"
                value={media.offerTitle || ""}
                onChange={(e: any) =>
                  patchMedia({ offerTitle: e.target.value })
                }
              />
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
                  Offer text
                </label>
                <textarea
                  value={media.offerText || ""}
                  onChange={(e) => patchMedia({ offerText: e.target.value })}
                  placeholder="Shown on the landing popup"
                  rows={3}
                  className="w-full resize-none rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827] outline-none transition placeholder:text-[#9CA3AF] focus:border-[#841C39] focus:ring-2 focus:ring-[#841C39]/20"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
                  Offer image
                </label>
                <input
                  ref={offerInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleOfferImage}
                  className="hidden"
                />
                {media.offerImageUrl ? (
                  <img
                    src={media.offerImageUrl}
                    alt="Offer"
                    className="mb-2 h-24 w-full rounded-lg object-cover"
                  />
                ) : null}
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={uploading}
                    onClick={() => offerInputRef.current?.click()}
                    className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-xs font-semibold text-[#374151] hover:bg-[#F9FAFB]"
                  >
                    {uploadingSlot === "offer" ? "Uploading…" : "Upload image"}
                  </button>
                  {media.offerImageUrl ? (
                    <button
                      type="button"
                      onClick={() => patchMedia({ offerImageUrl: "" })}
                      className="rounded-lg px-3 py-2 text-xs font-semibold text-[#DC2626]"
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              </div>
              <YhSelect
                label="Show offer on"
                value={media.offerScope || "all"}
                onChange={(e: any) =>
                  patchMedia({
                    offerScope:
                      e.target.value === "projects" ? "projects" : "all",
                  })
                }
                options={[
                  { label: "All Yhataw projects", value: "all" },
                  { label: "Selected projects", value: "projects" },
                ]}
              />
              {media.offerScope === "projects" ? (
                <YhMultiSelect
                  label="Projects"
                  placeholder="Select projects"
                  searchable
                  clearable
                  showSelectAll
                  options={projectOptions}
                  value={media.offerProjectIds || []}
                  onChange={(values) =>
                    patchMedia({
                      offerProjectIds: values.map((v) => String(v)),
                    })
                  }
                />
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <div>
        <p className="mb-2 text-sm font-semibold text-[#111827]">Form Theme</p>
        <div className="flex flex-wrap gap-2.5">
          {THEME_COLORS.map((color) => {
            const active = settings.theme === color;
            return (
              <button
                key={color}
                type="button"
                onClick={() => patch({ theme: color })}
                className={`h-8 w-8 rounded-full transition ${
                  active ? "ring-2 ring-offset-2 ring-[#841C39]" : ""
                }`}
                style={{ backgroundColor: color }}
                aria-label={`Theme ${color}`}
              />
            );
          })}
        </div>
      </div>

      {isPublicForm && (
        <div className="space-y-3 rounded-xl border border-[#F9F0F4] bg-[#FDFBFC] p-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-[#111827]">
              Side panel (image / video)
            </p>
          </div>
          <Toggle
            label="Show side panel"
            checked={media.enabled}
            onChange={(enabled) => patchMedia({ enabled })}
          />

          {media.enabled ? (
            <>
              <div>
                <p className="mb-1.5 text-xs font-semibold text-[#374151]">
                  Panel position
                </p>
                <div className="flex gap-2">
                  {(["left", "right"] as const).map((side) => (
                    <button
                      key={side}
                      type="button"
                      onClick={() => patchMedia({ position: side })}
                      className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold capitalize transition ${
                        media.position === side
                          ? "border-[#841C39] bg-[#F9F0F4] text-[#841C39]"
                          : "border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-[#F9FAFB]"
                      }`}
                    >
                      {side}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-1.5 text-xs font-semibold text-[#374151]">
                  Media type
                </p>
                <div className="flex gap-2">
                  {(["video", "image"] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => patchMedia({ mediaType: type })}
                      className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold capitalize transition ${
                        media.mediaType === type
                          ? "border-[#841C39] bg-[#F9F0F4] text-[#841C39]"
                          : "border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-[#F9FAFB]"
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {media.mediaType === "video" ? (
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-[#374151]">
                    Video file
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleMediaFile}
                    className="hidden"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1 rounded-lg border border-[#841C39] bg-white px-3 py-2 text-xs font-semibold text-[#841C39] transition hover:bg-[#F9F0F4] disabled:opacity-60"
                    >
                      {uploadingSlot === "video"
                        ? "Uploading…"
                        : media.mediaUrl
                          ? "Replace file"
                          : "Upload file"}
                    </button>
                    {media.mediaUrl ? (
                      <button
                        type="button"
                        onClick={() => patchMedia({ mediaUrl: "" })}
                        className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-xs font-semibold text-[#6B7280] transition hover:bg-[#F9FAFB]"
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                  {media.mediaUrl ? (
                    <div className="mt-2 overflow-hidden rounded-lg border border-[#E5E7EB] bg-black">
                      <video
                        src={media.mediaUrl}
                        className="h-28 w-full object-cover"
                        muted
                        loop
                        autoPlay
                        playsInline
                      />
                    </div>
                  ) : (
                    <p className="mt-1.5 text-[11px] text-[#6B7280]">
                      No file uploaded — the default Yhataw video is used.
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-lg border border-[#F3E8EE] bg-white px-3 py-2.5">
                    <p className="text-[11px] font-semibold text-[#841C39]">
                      Recommended image sizes (screen fit / cover)
                    </p>
                    <ul className="mt-1.5 space-y-1 text-[11px] leading-relaxed text-[#6B7280]">
                      <li>
                        <span className="font-semibold text-[#374151]">
                          Web:
                        </span>{" "}
                        1200 × 1600 px (3:4) or 1080 × 1920 — fills the desktop
                        side panel edge-to-edge
                      </li>
                      <li>
                        <span className="font-semibold text-[#374151]">
                          Mobile:
                        </span>{" "}
                        1080 × 1920 px (9:16) or 1080 × 1350 — fills the top
                        banner on phones
                      </li>
                      <li>
                        Export artwork cropped for each size so important text
                        stays in the safe center (edges may crop slightly).
                      </li>
                    </ul>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-[#374151]">
                      Web / desktop image
                    </label>
                    <p className="mb-1.5 text-[11px] text-[#6B7280]">
                      Used on screens wider than 920px. Covers the full side
                      panel — no background colour needed.
                    </p>
                    <input
                      ref={desktopInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleDesktopImage}
                      className="hidden"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={uploading}
                        onClick={() => desktopInputRef.current?.click()}
                        className="flex-1 rounded-lg border border-[#841C39] bg-white px-3 py-2 text-xs font-semibold text-[#841C39] transition hover:bg-[#F9F0F4] disabled:opacity-60"
                      >
                        {uploadingSlot === "desktop"
                          ? "Uploading…"
                          : desktopPreview
                            ? "Replace web image"
                            : "Upload web image"}
                      </button>
                      {desktopPreview ? (
                        <button
                          type="button"
                          onClick={() =>
                            patchMedia({
                              desktopImageUrl: "",
                              mediaUrl:
                                media.mediaUrl === desktopPreview
                                  ? ""
                                  : media.mediaUrl,
                            })
                          }
                          className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-xs font-semibold text-[#6B7280] transition hover:bg-[#F9FAFB]"
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                    {desktopPreview ? (
                      <div className="mt-2 overflow-hidden rounded-lg border border-[#E5E7EB] bg-[#F9FAFB]">
                        <img
                          src={desktopPreview}
                          alt="Web side panel"
                          className="h-28 w-full object-cover"
                        />
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-[#374151]">
                      Mobile image
                    </label>
                    <p className="mb-1.5 text-[11px] text-[#6B7280]">
                      Used on phones / narrow screens. Covers the top banner
                      band. Falls back to the web image if empty.
                    </p>
                    <input
                      ref={mobileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleMobileImage}
                      className="hidden"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={uploading}
                        onClick={() => mobileInputRef.current?.click()}
                        className="flex-1 rounded-lg border border-[#841C39] bg-white px-3 py-2 text-xs font-semibold text-[#841C39] transition hover:bg-[#F9F0F4] disabled:opacity-60"
                      >
                        {uploadingSlot === "mobile"
                          ? "Uploading…"
                          : mobilePreview
                            ? "Replace mobile image"
                            : "Upload mobile image"}
                      </button>
                      {mobilePreview ? (
                        <button
                          type="button"
                          onClick={() => patchMedia({ mobileImageUrl: "" })}
                          className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-xs font-semibold text-[#6B7280] transition hover:bg-[#F9FAFB]"
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                    {mobilePreview ? (
                      <div className="mt-2 overflow-hidden rounded-lg border border-[#E5E7EB] bg-[#F9FAFB]">
                        <img
                          src={mobilePreview}
                          alt="Mobile side panel"
                          className="h-28 w-full object-cover"
                        />
                      </div>
                    ) : (
                      <p className="mt-1.5 text-[11px] text-[#6B7280]">
                        Optional — without a mobile image, the web image is
                        used on all devices.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {PANEL_TEXT_FIELDS.map(
                ({ key, showKey, label, placeholder }) => {
                  const visible = media[showKey] !== false;
                  return (
                    <div key={key}>
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-[#374151]">
                          {label}
                        </span>
                        <button
                          type="button"
                          onClick={() => patchMedia({ [showKey]: !visible })}
                          className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide transition ${
                            visible
                              ? "bg-[#F9F0F4] text-[#841C39]"
                              : "bg-[#F3F4F6] text-[#9CA3AF]"
                          }`}
                        >
                          {visible ? "Shown" : "Hidden"}
                        </button>
                      </div>
                      <YhInput
                        value={media[key]}
                        onChange={(e: any) =>
                          patchMedia({ [key]: String(e?.target?.value ?? "") })
                        }
                        placeholder={placeholder}
                        containerClassName={visible ? "" : "opacity-50"}
                      />
                    </div>
                  );
                }
              )}
              <p className="text-[11px] leading-relaxed text-[#6B7280]">
                Leave a field blank to use the placeholder shown in it. Hiding
                keeps the text saved for later — useful when your image already
                shows it.
              </p>
            </>
          ) : null}

          <div className="border-t border-[#F3F4F6]">
            <Toggle
              label="Show form name as heading"
              checked={media.showFormTitle}
              onChange={(showFormTitle) => patchMedia({ showFormTitle })}
            />
          </div>
        </div>
      )}

      {isPublicForm && (
        <div>
          <p className="mb-1.5 text-sm font-semibold text-[#111827]">
            Form layout
          </p>
          <div className="flex gap-2">
            {(
              [
                { value: "steps", label: "One per page" },
                { value: "single", label: "All on one page" },
              ] as const
            ).map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => patch({ formLayout: opt.value })}
                className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                  (settings.formLayout || "steps") === opt.value
                    ? "border-[#841C39] bg-[#F9F0F4] text-[#841C39]"
                    : "border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-[#F9FAFB]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] leading-relaxed text-[#6B7280]">
            {(settings.formLayout || "steps") === "single"
              ? "Every field is shown together with a single Submit button. The progress bar and step counter are hidden."
              : "Asks one question at a time, advancing automatically after a choice."}
          </p>
        </div>
      )}

      {isPublicForm && (
        <div className="space-y-3 rounded-xl border border-[#F9F0F4] bg-[#FDFBFC] p-3">
          <p className="text-sm font-semibold text-[#111827]">
            Google Sheet sync
          </p>

          {!isGoogleSheetConnected ? (
            <div className="space-y-2">
              <button
                type="button"
                disabled={sheetConnecting}
                onClick={handleConnectGoogleSheet}
                className="w-full rounded-lg border border-[#841C39] bg-[#841C39] px-3 py-2.5 text-xs font-semibold text-white transition hover:bg-[#6e1730] disabled:opacity-60"
              >
                {sheetConnecting ? "Opening Google…" : "Connect Google Sheet"}
              </button>
              {sheetStatus && !sheetStatus.connected ? (
                <p className="text-[11px] leading-relaxed text-[#B45309]">
                  Google is not linked on the server yet. After you allow access,
                  you must land back on this form page (not Create Meeting).
                  Restart the API with GOOGLE_REDIRECT_URI +
                  GOOGLE_SHEETS_FRONTEND_CALLBACK set, then connect again.
                </p>
              ) : null}
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]">
                    Connected account
                  </p>
                  <p className="mt-0.5 truncate text-sm font-semibold text-[#111827]">
                    {sheetStatus?.googleEmail ||
                      googleSheet.connectedGoogleEmail ||
                      "Google connected"}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    onClick={handleConnectGoogleSheet}
                    className="text-[11px] font-semibold text-[#841C39] hover:underline"
                  >
                    Reconnect
                  </button>
                  <button
                    type="button"
                    onClick={handleDisconnectGoogleSheet}
                    className="text-[11px] font-semibold text-[#6B7280] hover:underline"
                  >
                    Disconnect
                  </button>
                </div>
              </div>

              <Toggle
                label="Sync submissions to Google Sheet"
                checked={!!googleSheet.enabled}
                onChange={(enabled) => patchGoogleSheet({ enabled })}
              />

              {linkedSheetUrl && !googleSheet.enabled ? (
                <a
                  href={linkedSheetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block truncate rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-[11px] font-semibold text-[#841C39] hover:border-[#841C39] hover:bg-[#F9F0F4]"
                >
                  Open linked sheet
                  {googleSheet.spreadsheetTitle
                    ? `: ${googleSheet.spreadsheetTitle}`
                    : ""}{" "}
                  ↗
                </a>
              ) : null}

              {googleSheet.enabled ? (
                <>
                  <div>
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <label className="text-xs font-semibold text-[#374151]">
                        Spreadsheet
                      </label>
                      <button
                        type="button"
                        disabled={creatingSheet || loadingSheets}
                        onClick={handleCreateSpreadsheet}
                        className="text-[11px] font-semibold text-[#841C39] hover:underline disabled:opacity-60"
                      >
                        {creatingSheet ? "Creating…" : "+ Create new sheet"}
                      </button>
                    </div>
                    <select
                      value={googleSheet.spreadsheetId || ""}
                      onChange={async (e) => {
                        const id = e.target.value;
                        const opt = spreadsheetOptions.find((o) => o.id === id);
                        patchGoogleSheet({
                          spreadsheetId: id,
                          spreadsheetTitle: opt?.name || "",
                          connectedGoogleEmail:
                            sheetStatus?.googleEmail ||
                            googleSheet.connectedGoogleEmail,
                          connectedUserId:
                            sheetStatus?.userId || googleSheet.connectedUserId,
                        });
                        await loadTabsFor(id);
                      }}
                      className="w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827] outline-none focus:border-[#841C39] focus:ring-2 focus:ring-[#841C39]/20"
                    >
                      <option value="">
                        {loadingSheets
                          ? "Loading your sheets…"
                          : "Select a spreadsheet"}
                      </option>
                      {googleSheet.spreadsheetId &&
                      !spreadsheetOptions.some(
                        (o) => o.id === googleSheet.spreadsheetId
                      ) ? (
                        <option value={googleSheet.spreadsheetId}>
                          {googleSheet.spreadsheetTitle ||
                            googleSheet.spreadsheetId}
                        </option>
                      ) : null}
                      {spreadsheetOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <button
                        type="button"
                        disabled={loadingSheets}
                        onClick={loadSpreadsheets}
                        className="text-[11px] font-semibold text-[#6B7280] hover:underline disabled:opacity-60"
                      >
                        Refresh list
                      </button>
                      {linkedSheetUrl ? (
                        <a
                          href={linkedSheetUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] font-semibold text-[#841C39] hover:underline"
                          title={
                            googleSheet.spreadsheetTitle ||
                            "Open linked Google Sheet"
                          }
                        >
                          Open sheet ↗
                        </a>
                      ) : null}
                    </div>
                    {linkedSheetUrl ? (
                      <a
                        href={linkedSheetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 block truncate rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-[11px] text-[#1E3A5F] hover:border-[#841C39] hover:bg-[#F9F0F4]"
                      >
                        {googleSheet.spreadsheetTitle
                          ? `${googleSheet.spreadsheetTitle} — `
                          : ""}
                        {linkedSheetUrl}
                      </a>
                    ) : null}
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-[#374151]">
                      Tab / sheet name
                    </label>
                    {tabOptions.length ? (
                      <select
                        value={googleSheet.sheetName || "Sheet1"}
                        onChange={(e) =>
                          patchGoogleSheet({ sheetName: e.target.value })
                        }
                        className="w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827] outline-none focus:border-[#841C39] focus:ring-2 focus:ring-[#841C39]/20"
                      >
                        {tabOptions.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <YhInput
                        value={googleSheet.sheetName}
                        onChange={(e: any) =>
                          patchGoogleSheet({
                            sheetName:
                              String(e?.target?.value ?? "") || "Sheet1",
                          })
                        }
                        placeholder="Sheet1"
                      />
                    )}
                  </div>

                  <button
                    type="button"
                    disabled={
                      sheetTesting || !googleSheet.spreadsheetId?.trim()
                    }
                    onClick={handleTestGoogleSheet}
                    className="w-full rounded-lg border border-[#841C39] bg-white px-3 py-2 text-xs font-semibold text-[#841C39] transition hover:bg-[#F9F0F4] disabled:opacity-60"
                  >
                    {sheetTesting ? "Testing…" : "Test connection"}
                  </button>
                </>
              ) : null}
            </>
          )}
        </div>
      )}

      {isPublicForm && (
        <div className="space-y-3 rounded-xl border border-[#F9F0F4] bg-[#FBFCFD] p-3">
          <p className="text-sm font-semibold text-[#111827]">
            Link preview &amp; SEO
          </p>
          <p className="text-[11px] leading-relaxed text-[#6B7280]">
            Controls the browser tab title and how the link looks when shared on
            WhatsApp, LinkedIn or X. Leave a field blank to use the fallback in
            its placeholder.
          </p>

          <YhInput
            label="Meta title"
            value={seo.metaTitle}
            onChange={(e: any) =>
              patchSeo({ metaTitle: String(e?.target?.value ?? "") })
            }
            placeholder="Form name · Organization"
          />
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
              Meta description
            </label>
            <textarea
              value={seo.metaDescription}
              onChange={(e) => patchSeo({ metaDescription: e.target.value })}
              placeholder="Falls back to the form description"
              rows={2}
              className="w-full resize-none rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827] outline-none transition placeholder:text-[#9CA3AF] focus:border-[#841C39] focus:ring-2 focus:ring-[#841C39]/20"
            />
          </div>

          <YhInput
            label="Share (OG) title"
            value={seo.ogTitle}
            onChange={(e: any) =>
              patchSeo({ ogTitle: String(e?.target?.value ?? "") })
            }
            placeholder="Same as meta title"
          />
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
              Share (OG) description
            </label>
            <textarea
              value={seo.ogDescription}
              onChange={(e) => patchSeo({ ogDescription: e.target.value })}
              placeholder="Same as meta description"
              rows={2}
              className="w-full resize-none rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827] outline-none transition placeholder:text-[#9CA3AF] focus:border-[#841C39] focus:ring-2 focus:ring-[#841C39]/20"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
              Share (OG) image
            </label>
            <input
              ref={ogInputRef}
              type="file"
              accept="image/*"
              onChange={handleOgFile}
              className="hidden"
            />
            <div className="flex gap-2">
              <button
                type="button"
                disabled={ogUploading}
                onClick={() => ogInputRef.current?.click()}
                className="flex-1 rounded-lg border border-[#841C39] bg-white px-3 py-2 text-xs font-semibold text-[#841C39] transition hover:bg-[#F9F0F4] disabled:opacity-60"
              >
                {ogUploading
                  ? "Uploading…"
                  : seo.ogImageUrl
                    ? "Replace image"
                    : "Upload image"}
              </button>
              {seo.ogImageUrl ? (
                <button
                  type="button"
                  onClick={() => patchSeo({ ogImageUrl: "" })}
                  className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-xs font-semibold text-[#6B7280] transition hover:bg-[#F9FAFB]"
                >
                  Remove
                </button>
              ) : null}
            </div>
            {seo.ogImageUrl ? (
              <div className="mt-2 overflow-hidden rounded-lg border border-[#E5E7EB] bg-black">
                <img
                  src={seo.ogImageUrl}
                  alt="Link preview"
                  className="h-28 w-full object-cover"
                />
              </div>
            ) : (
              <p className="mt-1.5 text-[11px] text-[#6B7280]">
                Falls back to the side panel image. Best at 1200×630.
              </p>
            )}
          </div>
        </div>
      )}

      {isPublicForm && (
        <div className="space-y-3 rounded-xl border border-[#F9F0F4] bg-[#F8FAFF] p-3">
          <YhInput
            label="Customer link expiry (days)"
            type="number"
            value={String(settings.tokenMaxDays ?? 90)}
            onChange={(e: any) => {
              const n = Number(e?.target?.value ?? e);
              patch({
                tokenMaxDays: Number.isFinite(n) ? Math.max(0, n) : 0,
              });
            }}
            placeholder="0 = no expiry"
          />
          <p className="text-[11px] leading-relaxed text-[#6B7280]">
            Guest link (no token) = anonymous visitor. Per-lead token links
            track which customer filled the form. Public path:{" "}
            <code className="rounded bg-white px-1">
              /{settings.formType === "registration" ? "registration" : "feedback"}
              /&#123;org&#125;/&#123;form&#125;
            </code>
            {settings.defaultLeadSource ? (
              <>
                {" "}
                with{" "}
                <code className="rounded bg-white px-1">
                  ?src={settings.defaultLeadSource}
                </code>
              </>
            ) : null}
          </p>
          {publicLink ? (
            <FormShareLinkList
              compact
              form={{
                publicUrl: settings.publicUrl,
                orgSlug: settings.orgSlug,
                projectSlug: settings.projectSlug,
                publicSlug: settings.publicSlug,
                formType: settings.formType,
                defaultLeadSource: settings.defaultLeadSource,
              }}
            />
          ) : (
            <p className="text-[11px] text-[#9CA3AF]">
              Save the form to generate the public link.
            </p>
          )}
        </div>
      )}

      <div className="divide-y divide-[#F3F4F6] border-t border-[#F3F4F6] pt-1">
        {settings.formType === "website" ? (
          <p className="py-2.5 text-sm text-[#6B7280]">
            Website form stays active. It cannot be turned off or deleted.
          </p>
        ) : (
          <Toggle
            label="Enable Form"
            checked={settings.enableForm}
            onChange={(enableForm) => patch({ enableForm })}
          />
        )}
        {(settings.formType === "registration" ||
          settings.formType === "feedback") && (
          <div className="space-y-2 py-2.5">
            <Toggle
              label={
                settings.formType === "registration"
                  ? "Send WhatsApp welcome on registration"
                  : "Send WhatsApp thank-you on feedback submit"
              }
              checked={!!settings.sendWhatsAppOnSubmit}
              onChange={(sendWhatsAppOnSubmit) =>
                patch({ sendWhatsAppOnSubmit })
              }
            />
            {settings.sendWhatsAppOnSubmit ? (
              <div className="rounded-xl border border-[#F9F0F4] bg-[#FDF8FA] p-3">
                <YhSelect
                  label={
                    settings.formType === "registration"
                      ? "Welcome WhatsApp template (Engage)"
                      : "Thank-you WhatsApp template (Engage)"
                  }
                  requiredMark
                  clearable
                  searchable
                  placeholder={
                    loadingWaTemplates
                      ? "Loading templates…"
                      : organizationId
                        ? "Select Engage template"
                        : "Select organization first"
                  }
                  value={settings.onSubmitWhatsappTemplateWid || ""}
                  options={waTemplates}
                  disabled={!organizationId || loadingWaTemplates}
                  onChange={(e: any) => {
                    const wid = String(e?.target?.value || "");
                    const match = waTemplates.find((t) => t.value === wid);
                    patch({
                      onSubmitWhatsappTemplateWid: wid,
                      onSubmitWhatsappTemplateName: match?.name || "",
                    });
                  }}
                />
                <p className="mt-1.5 text-[11px] leading-relaxed text-[#6B7280]">
                  {settings.formType === "registration"
                    ? "Create your welcome template in Engage (e.g. YHATAW Realty Collective welcome), then select it here. Sent only after a successful registration — does not create CRM leads."
                    : "Create your thank-you / feedback template in Engage (include the link variable in Engage if needed), then select it here. Sent after feedback is submitted."}
                </p>
              </div>
            ) : null}
          </div>
        )}
        <Toggle
          label="Show Progress Bar"
          checked={settings.showProgressBar}
          onChange={(showProgressBar) => patch({ showProgressBar })}
        />
        <Toggle
          label="Allow Multiple Submissions"
          checked={settings.allowMultiple}
          onChange={(allowMultiple) => patch({ allowMultiple })}
        />
        <Toggle
          label="Enable CAPTCHA"
          checked={settings.enableCaptcha}
          onChange={(enableCaptcha) => patch({ enableCaptcha })}
        />
        <Toggle
          label="Payment Method"
          checked={settings.enablePaymentMethod}
          onChange={(enablePaymentMethod) => patch({ enablePaymentMethod })}
        />
        <Toggle
          label="Aadhaar Verification"
          checked={settings.enableAadhaarVerification}
          onChange={(enableAadhaarVerification) =>
            patch({ enableAadhaarVerification })
          }
        />
      </div>

      <div className="mt-auto rounded-xl border border-[#F9F0F4] bg-[#F5F3FF] p-3 text-xs leading-relaxed text-[#5B21B6]">
        Tip: For feedback/registration forms, add text, phone, email, select,
        radio, or rating fields. Drag from the left panel, then configure in
        Field Settings.
      </div>
    </div>
  );
};

export default FormSettingsPanel;
